import { useId } from 'react'
import { Briefcase } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useChangerDossierClio, useDossiersClio, type DossierClio as Dossier } from '@/api/clio'

interface Props {
  callId: number
  phoneNumber: string
  darkMode: boolean
  /** « details » : rangée du panneau Détails (ordinateur) ; « carte » : bloc de la carte du fil (mobile seulement). */
  variante: 'details' | 'carte'
}

/**
 * Le dossier Clio d'un appel, quand le client en a plusieurs ouverts (ou que
 * l'appel n'en a pas encore). Callizy prend le plus récent ; le cabinet peut
 * en choisir un autre, la communication et le temps suivent dans Clio.
 */
export function DossierClio({ callId, phoneNumber, darkMode, variante }: Props) {
  const { t } = useTranslation()
  const id = useId()
  const { data } = useDossiersClio(phoneNumber)
  const changer = useChangerDossierClio(phoneNumber)
  const appel = data?.calls[String(callId)]
  if (!data || !appel) return null
  if (data.matters.length < 2 && !(appel.matter_id === '' && data.matters.length > 0)) return null

  const libelle = (d: Dossier) => {
    const mode = ['hourly', 'flat', 'contingency'].includes(d.billing_method) ? t(`callDetail.clio.modes.${d.billing_method}`) : ''
    return [d.display_number, d.description].filter(Boolean).join(' · ') + (mode ? ` (${mode})` : '')
  }
  const connu = data.matters.some((d) => d.id === appel.matter_id)
  const choisir = (matterId: string) => {
    const dossier = data.matters.find((d) => d.id === matterId)
    changer.mutate(
      { callId, matterId },
      {
        onSuccess: () => toast.success(t('callDetail.clio.deplace', { dossier: dossier?.display_number || '' })),
        onError: () => toast.error(t('callDetail.clio.erreur')),
      },
    )
  }
  const sourdine = darkMode ? 'text-slate-400' : 'text-slate-500'
  const options = (
    <>
      {appel.matter_id === '' && <option value="" disabled>{t('callDetail.clio.choisir')}</option>}
      {appel.matter_id !== '' && !connu && <option value={appel.matter_id}>{t('callDetail.clio.ferme')}</option>}
      {data.matters.map((d) => (
        <option key={d.id} value={d.id}>{libelle(d)}</option>
      ))}
    </>
  )
  const mention = appel.auto && appel.matter_id !== '' ? t('callDetail.clio.auto') : ''
  const champ = cn(
    'min-w-0 rounded-lg border disabled:opacity-60',
    darkMode ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-800',
  )

  if (variante === 'details') {
    // Ordinateur : une rangée comme Date, Répondu par, Ligne ; la mention en dessous, alignée sur la valeur.
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2.5 text-[12px]">
          <label htmlFor={id} className={cn('flex items-center gap-1.5 w-[92px] flex-shrink-0', sourdine)}>
            <Briefcase className="h-3.5 w-3.5" />
            {t('callDetail.clio.titre')}
          </label>
          <select
            id={id}
            value={appel.matter_id}
            onChange={(e) => choisir(e.target.value)}
            disabled={changer.isPending}
            className={cn(champ, 'flex-1 px-2 py-1 text-[12px]')}
          >
            {options}
          </select>
        </div>
        {mention && <p className={cn('pl-[102px] text-[11px] leading-snug', sourdine)}>{mention}</p>}
      </div>
    )
  }

  // Mobile : le libellé au-dessus, une liste pleine largeur assez haute pour le doigt
  // (16 px : iOS ne zoome pas au toucher). Le panneau Détails n'existe pas sur mobile.
  return (
    <div className={cn('md:hidden mx-3 mb-3 pt-2.5 border-t flex flex-col gap-1.5', darkMode ? 'border-slate-700/60' : 'border-gray-100')}>
      <label htmlFor={id} className={cn('flex items-center gap-1.5 text-[12px] font-medium', sourdine)}>
        <Briefcase className="h-3.5 w-3.5" />
        {t('callDetail.clio.titre')}
      </label>
      <select
        id={id}
        value={appel.matter_id}
        onChange={(e) => choisir(e.target.value)}
        disabled={changer.isPending}
        className={cn(champ, 'w-full px-3 py-2.5 text-base')}
      >
        {options}
      </select>
      {mention && <p className={cn('text-[11.5px] leading-snug', sourdine)}>{mention}</p>}
    </div>
  )
}
